import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import InstallationDeploymentTab from '../components/installation/InstallationDeploymentTab';
import SiteInspectionTab from '../components/installation/SiteInspectionTab';
import CustomerSignOffsTab from '../components/installation/CustomerSignOffsTab';
import InstallationCalendarTab from '../components/installation/InstallationCalendarTab';
import ServiceClientsTab from '../components/service/ServiceClientsTab';
import CallLogTab from '../components/service/CallLogTab';
import OnsiteServiceTab from '../components/service/OnsiteServiceTab';
import './Page.css';
import './TasksBoard.css';
import './Events.css';
import './Elevators.css';
import './Installation.css';
import './ServiceSupport.css';

const TABS = [
  { id: 'service-support', label: 'Service and Support' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'site-inspection', label: 'Site Inspection' },
  { id: 'deployment', label: 'Installation & Deployment' },
  { id: 'sign-offs', label: 'Customer Sign-Offs' },
] as const;

type TabId = (typeof TABS)[number]['id'];

/** Sections inside the Service and Support tab, selected with `?section=`. */
const SERVICE_SECTIONS = [
  { id: 'clients', label: 'Clients' },
  { id: 'call-log', label: 'Call Log' },
  { id: 'onsite-service', label: 'Onsite Service' },
] as const;

type ServiceSectionId = (typeof SERVICE_SECTIONS)[number]['id'];

const Installation: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo<TabId>(() => {
    const t = searchParams.get('tab');
    return TABS.some((tab) => tab.id === t) ? (t as TabId) : 'calendar';
  }, [searchParams]);

  const activeSection = useMemo<ServiceSectionId>(() => {
    const s = searchParams.get('section');
    return SERVICE_SECTIONS.some((sec) => sec.id === s) ? (s as ServiceSectionId) : 'clients';
  }, [searchParams]);

  const selectTab = useCallback(
    (tab: TabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          // The inner section only means anything under Service and Support.
          if (tab !== 'service-support') next.delete('section');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const selectSection = useCallback(
    (section: ServiceSectionId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', 'service-support');
          next.set('section', section);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader
            title="Installation"
            subtitle="Service and support, site inspections, installation & deployment, and customer sign-offs"
          />
          <div className="page-content">
            {!user ? (
              <p className="page-subtitle">Log in to view appointments.</p>
            ) : (
              <>
                <div className="installation-subtabs" role="tablist" aria-label="Installation sections">
                  {TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      className={`installation-subtab${activeTab === tab.id ? ' installation-subtab--active' : ''}`}
                      onClick={() => selectTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {activeTab === 'service-support' ? (
                  <>
                    <div
                      className="service-support-sections"
                      role="tablist"
                      aria-label="Service and support sections"
                    >
                      {SERVICE_SECTIONS.map((section) => (
                        <button
                          key={section.id}
                          type="button"
                          role="tab"
                          aria-selected={activeSection === section.id}
                          className={`service-support-section${
                            activeSection === section.id ? ' service-support-section--active' : ''
                          }`}
                          onClick={() => selectSection(section.id)}
                        >
                          {section.label}
                        </button>
                      ))}
                    </div>

                    {activeSection === 'clients' ? <ServiceClientsTab /> : null}
                    {activeSection === 'call-log' ? <CallLogTab /> : null}
                    {activeSection === 'onsite-service' ? <OnsiteServiceTab /> : null}
                  </>
                ) : null}

                {activeTab === 'calendar' ? <InstallationCalendarTab /> : null}
                {activeTab === 'site-inspection' ? <SiteInspectionTab /> : null}
                {activeTab === 'deployment' ? <InstallationDeploymentTab /> : null}
                {activeTab === 'sign-offs' ? <CustomerSignOffsTab /> : null}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Installation;
